import React from "react";
import { Helmet } from "react-helmet-async";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import { SectionHeading } from "components/misc/Headings";
import { Link } from "react-router-dom";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10`;
const Text = styled.div`
  ${tw`text-lg  text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

export default ({ headingText = "Terms & Conditions" }) => {
  return (
    <AnimationRevealPage>
      <Helmet>
        <title>Terms of Service | EthosHub</title>
        <meta name="description" content="The terms governing use of EthosHub's anonymous workplace reporting platform for organisations and their employees." />
        <link rel="canonical" href="https://www.ethoshub.io/terms-of-service" />
        <meta property="og:title" content="Terms of Service | EthosHub" />
        <meta property="og:description" content="The terms governing use of EthosHub's anonymous workplace reporting platform for organisations and their employees." />
        <meta property="og:url" content="https://www.ethoshub.io/terms-of-service" />
      </Helmet>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>{headingText}</Heading>
          </HeadingRow>
          <Text>
            <h1>Terms of Service</h1>
            <p>Effective Date: 12th June 2025</p>

            <p>
              Welcome to EthosHub.io, a service provided by Inspired Cloud Solutions Ltd ("Inspired Cloud Solutions",
              "EthosHub", "we", "us", or "our"), a company registered in England and Wales.
            </p>

            <p>
              These Terms of Service ("Terms") govern your access to and use of our platform, applications, and
              services (the "Service"). By using EthosHub, you agree to be bound by these Terms.
            </p>

            <h2>1. Who We Serve</h2>
            <p>EthosHub is designed for:</p>
            <ul>
              <li>Organisations using our platform to manage workplace reports</li>
              <li>Caseworkers and administrators managing incidents and users</li>
              <li>Employees submitting reports, with or without identification</li>
            </ul>

            <h2>2. Use of the Service</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms.</p>
            <p>You must not:</p>
            <ul>
              <li>Access the Service using false credentials</li>
              <li>Submit false or malicious reports</li>
              <li>Attempt to disrupt the Service or its users</li>
              <li>Reverse-engineer or copy any part of the Service</li>
            </ul>

            <h2>3. User Accounts</h2>
            <p>To access certain features, you may be required to create an account. You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your login details</li>
              <li>All activities that occur under your account</li>
            </ul>
            <p>
              You must notify us immediately of any unauthorised use or suspected breach of your account at{" "}
              <a href="mailto:support@ethoshub.io">support@ethoshub.io</a>.
            </p>

            <h2>4. Anonymous Reporting</h2>
            <p>
              Our platform allows anonymous submissions. We take deliberate technical steps to protect anonymity,
              including not logging IP addresses for anonymous reports and storing only a hashed session token. While
              we make every effort to ensure anonymity, you are responsible for the content you submit.
            </p>

            <h2>5. Data Privacy and Protection</h2>
            <p>
              We process your personal data in accordance with our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link> and{" "}
              <Link to="/gdpr">GDPR Policy</Link>.
            </p>
            <p>
              Organisations using EthosHub act as data controllers in respect of their employees' data. EthosHub acts
              as a data processor on their behalf. Subscribing organisations are responsible for complying with
              applicable data protection laws when handling submitted reports and user data.
            </p>

            <h2>6. Intellectual Property</h2>
            <p>
              All content, features, and software on the Service are owned by Inspired Cloud Solutions Ltd or its
              licensors and are protected by copyright and other intellectual property laws.
            </p>
            <p>You may not:</p>
            <ul>
              <li>Reproduce, redistribute, or modify the Service</li>
              <li>Use any EthosHub branding without prior written permission</li>
            </ul>

            <h2>7. Availability and Support</h2>
            <p>
              We strive to maintain uninterrupted access to the Service but do not guarantee it. We may suspend
              access for maintenance, upgrades, or in the event of a security risk. We will endeavour to provide
              advance notice of planned maintenance where possible.
            </p>
            <p>
              Email support is available via <a href="mailto:support@ethoshub.io">support@ethoshub.io</a>.
            </p>

            <h2>8. Pricing and Subscriptions</h2>
            <p>
              Subscriptions to EthosHub are billed according to your selected plan and organisation size. All
              features are included in every subscription tier — we do not charge per feature. Pricing is available
              on our <Link to="/PricingPage">Pricing Page</Link>.
            </p>
            <p>
              Subscriptions may be cancelled at any time. Cancellation takes effect at the end of the current billing
              period. We do not offer refunds for partial periods unless required by law.
            </p>

            <h2>9. Termination</h2>
            <p>
              We reserve the right to suspend or terminate access to the Service for violations of these Terms or
              misuse of the platform, with or without notice depending on the severity of the breach. You may cancel
              your account or subscription at any time through the billing settings in your account.
            </p>

            <h2>10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, Inspired Cloud Solutions Ltd shall not be liable
              for:
            </p>
            <ul>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Loss of data or loss of profit</li>
              <li>Misuse of the platform by users or third parties</li>
            </ul>
            <p>
              Nothing in these Terms excludes or limits our liability for death or personal injury caused by
              negligence, fraud, or any other liability that cannot be excluded by law.
            </p>

            <h2>11. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify users by
              email or via the platform with reasonable advance notice. Continued use of the Service after changes
              take effect constitutes acceptance of the updated Terms.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any
              disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>

            <h2>13. Contact Us</h2>
            <p>For any questions about these Terms, please contact:</p>
            <p>
              <strong>Inspired Cloud Solutions Ltd</strong> (trading as EthosHub)<br />
              Email: <a href="mailto:support@ethoshub.io">support@ethoshub.io</a><br />
              Website: <a href="https://www.ethoshub.io">www.ethoshub.io</a>
            </p>
          </Text>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};
